import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDetailBack } from '../../../hooks/useDetailBack';
import inventoryApi from '../../../services/inventoryApi';;

const RouteForm = ({ route, onSuccess, onCancel }) => {
  const navigate = useNavigate();
  const handleBack = useDetailBack('/inventory/distribution-routes');
  const { id } = useParams();
  const isEditMode = Boolean(id && id !== 'new');

  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    type: 'DELIVERY',
    status: 'ACTIVE',
    startLocation: {
      name: '',
      address: '',
      coordinates: { type: 'Point', coordinates: [] }
    },
    endLocation: {
      name: '',
      address: '',
      coordinates: { type: 'Point', coordinates: [] }
    },
    estimatedDistance: '',
    estimatedDuration: '',
    maxStops: 10,
    stops: [],
    assignedVehicles: [],
    assignedDrivers: [],
    workingDays: ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'],
    maxWeight: '',
    maxVolume: '',
    priority: 'MEDIUM',
    isActive: true
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const routeTypes = [
    { value: 'DELIVERY', label: 'Delivery Route' },
    { value: 'PICKUP', label: 'Pickup Route' },
    { value: 'ROUND_TRIP', label: 'Round Trip' },
    { value: 'ONE_WAY', label: 'One Way' },
    { value: 'EMERGENCY', label: 'Emergency Route' }
  ];

  const routeStatuses = [
    { value: 'ACTIVE', label: 'Active' },
    { value: 'INACTIVE', label: 'Inactive' },
    { value: 'MAINTENANCE', label: 'Under Maintenance' },
    { value: 'SUSPENDED', label: 'Suspended' }
  ];

  const priorityLevels = [
    { value: 'LOW', label: 'Low Priority' },
    { value: 'MEDIUM', label: 'Medium Priority' },
    { value: 'HIGH', label: 'High Priority' },
    { value: 'URGENT', label: 'Urgent' }
  ];

  const daysOfWeek = [
    { value: 'MONDAY', label: 'Monday' },
    { value: 'TUESDAY', label: 'Tuesday' },
    { value: 'WEDNESDAY', label: 'Wednesday' },
    { value: 'THURSDAY', label: 'Thursday' },
    { value: 'FRIDAY', label: 'Friday' },
    { value: 'SATURDAY', label: 'Saturday' },
    { value: 'SUNDAY', label: 'Sunday' }
  ];

  useEffect(() => {
    if (route) {
      setFormData({
        ...route,
        startLocation: route.startLocation || { name: '', address: '', coordinates: { type: 'Point', coordinates: [] } },
        endLocation: route.endLocation || { name: '', address: '', coordinates: { type: 'Point', coordinates: [] } }
      });
    } else if (isEditMode && id) {
      fetchRoute();
    }
  }, [route, id]);

  const fetchRoute = async () => {
    try {
      setLoading(true);
      const response = await inventoryApi.getRouteById(id);
      setFormData(response.data);
    } catch (err) {
      setErrors({ submit: err.response?.data?.message || 'Failed to fetch route' });
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Route code is required';
    } else if (!/^[A-Z0-9\-_]{2,20}$/.test(formData.code)) {
      newErrors.code = 'Code must be 2-20 characters, uppercase alphanumeric with dashes/underscores';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Route name is required';
    }

    if (!formData.startLocation.name.trim()) {
      newErrors['startLocation.name'] = 'Start location name is required';
    }

    if (!formData.endLocation.name.trim()) {
      newErrors['endLocation.name'] = 'End location name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value
      }
    }));

    if (errors[`${parent}.${field}`]) {
      setErrors(prev => ({ ...prev, [`${parent}.${field}`]: '' }));
    }
  };

  const handleWorkingDaysChange = (day) => {
    setFormData(prev => ({
      ...prev,
      workingDays: prev.workingDays.includes(day)
        ? prev.workingDays.filter(d => d !== day)
        : [...prev.workingDays, day]
    }));
  };

  const addStop = () => {
    setFormData(prev => ({
      ...prev,
      stops: [
        ...prev.stops,
        {
          sequence: prev.stops.length + 1,
          name: '',
          address: '',
          estimatedTime: 15,
          coordinates: { type: 'Point', coordinates: [] }
        }
      ]
    }));
  };

  const removeStop = (index) => {
    setFormData(prev => ({
      ...prev,
      stops: prev.stops.filter((_, i) => i !== index).map((stop, i) => ({ ...stop, sequence: i + 1 }))
    }));
  };

  const handleStopChange = (index, field, value) => {
    const updatedStops = [...formData.stops];
    updatedStops[index] = { ...updatedStops[index], [field]: value };
    setFormData(prev => ({ ...prev, stops: updatedStops }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        code: formData.code.toUpperCase()
      };

      if (route || isEditMode) {
        await inventoryApi.updateRoute(route?._id || id, submitData);
      } else {
        await inventoryApi.createRoute(submitData);
      }
      
      if (onSuccess) {
        onSuccess();
      } else {
        handleBack();
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setErrors({ submit: error.response?.data?.message || error.message || 'Failed to save route' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      handleBack();
    }
  };

  return (
    <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-2">
      <div className="relative w-full max-w-5xl bg-white rounded-2xl shadow-2xl border border-gray-100 max-h-[95vh] overflow-y-auto">
        {/* Compact Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-violet-600 rounded-t-2xl px-4 py-3 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-white">
                {route || isEditMode ? 'Edit Route' : 'Create New Route'}
              </h3>
            </div>
            <button
              onClick={handleCancel}
              className="w-7 h-7 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-4">
          {errors.submit && (
            <div className="mb-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-center">
                <svg className="h-4 w-4 text-red-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm font-medium text-red-800">{errors.submit}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Main Form Grid - Compact 2 columns */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Left Column */}
              <div className="space-y-3">
                {/* Route Code */}
                <div>
                  <label htmlFor="code" className="block text-sm font-semibold text-gray-800 mb-1">
                    Route Code *
                  </label>
                  <input
                    type="text"
                    id="code"
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    disabled={!!(route || isEditMode)}
                    placeholder="e.g., ROUTE001"
                    className={`block w-full rounded-lg border-0 bg-gray-50 py-2.5 px-3 text-sm text-gray-900 shadow-sm ring-1 ring-inset transition-all uppercase ${
                      errors.code 
                        ? 'ring-red-300 focus:ring-red-500 bg-red-50' 
                        : 'ring-gray-200 hover:ring-gray-300 focus:ring-2 focus:ring-purple-500 hover:bg-white'
                    } ${(route || isEditMode) ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  />
                  {errors.code && <p className="mt-1 text-xs text-red-600">{errors.code}</p>}
                </div>

                {/* Route Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-800 mb-1">
                    Route Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="e.g., Downtown Delivery Route"
                    className={`block w-full rounded-lg border-0 bg-gray-50 py-2.5 px-3 text-sm text-gray-900 shadow-sm ring-1 ring-inset transition-all ${
                      errors.name 
                        ? 'ring-red-300 focus:ring-red-500 bg-red-50' 
                        : 'ring-gray-200 hover:ring-gray-300 focus:ring-2 focus:ring-purple-500 hover:bg-white'
                    }`}
                  />
                  {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
                </div>

                {/* Route Type & Status */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label htmlFor="type" className="block text-sm font-semibold text-gray-800 mb-1">
                      Route Type
                    </label>
                    <select
                      id="type"
                      name="type"
                      value={formData.type}
                      onChange={handleChange}
                      className="block w-full rounded-lg border-0 bg-gray-50 py-2.5 px-3 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 hover:ring-gray-300 focus:ring-2 focus:ring-purple-500 hover:bg-white transition-all"
                    >
                      {routeTypes.map(type => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="status" className="block text-sm font-semibold text-gray-800 mb-1">
                      Status
                    </label>
                    <select
                      id="status"
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      className="block w-full rounded-lg border-0 bg-gray-50 py-2.5 px-3 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 hover:ring-gray-300 focus:ring-2 focus:ring-purple-500 hover:bg-white transition-all"
                    >
                      {routeStatuses.map(status => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Start Location */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">Start Location</h4>
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder="Location Name *"
                      value={formData.startLocation.name}
                      onChange={(e) => handleNestedChange('startLocation', 'name', e.target.value)}
                      className={`block w-full rounded-lg border-0 bg-gray-50 py-2 px-3 text-sm text-gray-900 shadow-sm ring-1 ring-inset transition-all ${
                        errors['startLocation.name'] 
                          ? 'ring-red-300 focus:ring-red-500 bg-red-50' 
                          : 'ring-gray-200 hover:ring-gray-300 focus:ring-2 focus:ring-purple-500 hover:bg-white'
                      }`}
                    />
                    <input
                      type="text"
                      placeholder="Address"
                      value={formData.startLocation.address}
                      onChange={(e) => handleNestedChange('startLocation', 'address', e.target.value)}
                      className="block w-full rounded-lg border-0 bg-gray-50 py-2 px-3 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 hover:ring-gray-300 focus:ring-2 focus:ring-purple-500 hover:bg-white transition-all"
                    />
                  </div>
                  {errors['startLocation.name'] && <p className="mt-1 text-xs text-red-600">{errors['startLocation.name']}</p>}
                </div>

                {/* End Location */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">End Location</h4>
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder="Location Name *"
                      value={formData.endLocation.name}
                      onChange={(e) => handleNestedChange('endLocation', 'name', e.target.value)}
                      className={`block w-full rounded-lg border-0 bg-gray-50 py-2 px-3 text-sm text-gray-900 shadow-sm ring-1 ring-inset transition-all ${
                        errors['endLocation.name'] 
                          ? 'ring-red-300 focus:ring-red-500 bg-red-50' 
                          : 'ring-gray-200 hover:ring-gray-300 focus:ring-2 focus:ring-purple-500 hover:bg-white'
                      }`}
                    />
                    <input
                      type="text"
                      placeholder="Address"
                      value={formData.endLocation.address}
                      onChange={(e) => handleNestedChange('endLocation', 'address', e.target.value)}
                      className="block w-full rounded-lg border-0 bg-gray-50 py-2 px-3 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 hover:ring-gray-300 focus:ring-2 focus:ring-purple-500 hover:bg-white transition-all"
                    />
                  </div>
                  {errors['endLocation.name'] && <p className="mt-1 text-xs text-red-600">{errors['endLocation.name']}</p>}
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-3">
                {/* Description */}
                <div>
                  <label htmlFor="description" className="block text-sm font-semibold text-gray-800 mb-1">
                    Description
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    rows={3}
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Brief description of this route..."
                    className="block w-full rounded-lg border-0 bg-gray-50 py-2.5 px-3 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 hover:ring-gray-300 focus:ring-2 focus:ring-purple-500 hover:bg-white transition-all resize-none"
                  />
                </div>

                {/* Route Details */}
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label htmlFor="estimatedDistance" className="block text-sm font-semibold text-gray-800 mb-1">
                      Distance (km)
                    </label>
                    <input
                      type="number"
                      id="estimatedDistance"
                      name="estimatedDistance"
                      value={formData.estimatedDistance}
                      onChange={handleChange}
                      placeholder="0"
                      min="0"
                      step="0.1"
                      className="block w-full rounded-lg border-0 bg-gray-50 py-2 px-3 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 hover:ring-gray-300 focus:ring-2 focus:ring-purple-500 hover:bg-white transition-all"
                    />
                  </div>
                  <div>
                    <label htmlFor="estimatedDuration" className="block text-sm font-semibold text-gray-800 mb-1">
                      Duration (min)
                    </label>
                    <input
                      type="number"
                      id="estimatedDuration"
                      name="estimatedDuration"
                      value={formData.estimatedDuration}
                      onChange={handleChange}
                      placeholder="0"
                      min="0"
                      className="block w-full rounded-lg border-0 bg-gray-50 py-2 px-3 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 hover:ring-gray-300 focus:ring-2 focus:ring-purple-500 hover:bg-white transition-all"
                    />
                  </div>
                </div>

                {/* Capacity Limits */}
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label htmlFor="maxStops" className="block text-sm font-semibold text-gray-800 mb-1">
                      Max Stops
                    </label>
                    <input
                      type="number"
                      id="maxStops"
                      name="maxStops"
                      value={formData.maxStops}
                      onChange={handleChange}
                      min="1"
                      className="block w-full rounded-lg border-0 bg-gray-50 py-2 px-3 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 hover:ring-gray-300 focus:ring-2 focus:ring-purple-500 hover:bg-white transition-all"
                    />
                  </div>
                  <div>
                    <label htmlFor="maxWeight" className="block text-sm font-semibold text-gray-800 mb-1">
                      Max Weight (kg)
                    </label>
                    <input
                      type="number"
                      id="maxWeight"
                      name="maxWeight"
                      value={formData.maxWeight}
                      onChange={handleChange}
                      placeholder="0"
                      min="0"
                      className="block w-full rounded-lg border-0 bg-gray-50 py-2 px-3 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 hover:ring-gray-300 focus:ring-2 focus:ring-purple-500 hover:bg-white transition-all"
                    />
                  </div>
                  <div>
                    <label htmlFor="priority" className="block text-sm font-semibold text-gray-800 mb-1">
                      Priority
                    </label>
                    <select
                      id="priority"
                      name="priority"
                      value={formData.priority}
                      onChange={handleChange}
                      className="block w-full rounded-lg border-0 bg-gray-50 py-2 px-3 text-sm text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 hover:ring-gray-300 focus:ring-2 focus:ring-purple-500 hover:bg-white transition-all"
                    >
                      {priorityLevels.map(priority => (
                        <option key={priority.value} value={priority.value}>
                          {priority.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Working Days */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">Working Days</h4>
                  <div className="grid grid-cols-4 gap-2">
                    {daysOfWeek.map(day => (
                      <label key={day.value} className="flex items-center space-x-2 p-2 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.workingDays.includes(day.value)}
                          onChange={() => handleWorkingDaysChange(day.value)}
                          className="h-3 w-3 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                        />
                        <span className="text-xs text-gray-700">{day.label.slice(0, 3)}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Active Status */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <input
                      id="isActive"
                      name="isActive"
                      type="checkbox"
                      checked={formData.isActive}
                      onChange={handleChange}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <label htmlFor="isActive" className="ml-2 block text-sm font-medium text-gray-900">
                      Route Active
                    </label>
                  </div>
                  <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    formData.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {formData.isActive ? 'Active' : 'Inactive'}
                  </div>
                </div>
              </div>
            </div>

            {/* Stops Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-gray-800">Route Stops</h4>
                <button
                  type="button"
                  onClick={addStop}
                  className="px-3 py-1.5 text-xs bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
                >
                  Add Stop
                </button>
              </div>
              
              <div className="space-y-2">
                {formData.stops.map((stop, index) => (
                  <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-xs font-semibold">
                      {stop.sequence}
                    </div>
                    <input
                      type="text"
                      placeholder="Stop name"
                      value={stop.name}
                      onChange={(e) => handleStopChange(index, 'name', e.target.value)}
                      className="flex-1 rounded-lg border-0 bg-white py-1.5 px-2 text-xs text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 focus:ring-2 focus:ring-purple-500"
                    />
                    <input
                      type="text"
                      placeholder="Address"
                      value={stop.address}
                      onChange={(e) => handleStopChange(index, 'address', e.target.value)}
                      className="flex-1 rounded-lg border-0 bg-white py-1.5 px-2 text-xs text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 focus:ring-2 focus:ring-purple-500"
                    />
                    <input
                      type="number"
                      placeholder="Time (min)"
                      value={stop.estimatedTime}
                      onChange={(e) => handleStopChange(index, 'estimatedTime', parseInt(e.target.value) || 0)}
                      min="1"
                      className="w-20 rounded-lg border-0 bg-white py-1.5 px-2 text-xs text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 focus:ring-2 focus:ring-purple-500"
                    />
                    <button
                      type="button"
                      onClick={() => removeStop(index)}
                      className="p-1.5 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                    >
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex flex-col-reverse sm:flex-row gap-3 pt-6 border-t border-gray-100">
              <button
                type="button"
                onClick={handleCancel}
                className="flex-1 px-4 py-2.5 text-gray-700 bg-gray-100 hover:bg-gray-200 font-medium rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 text-white font-medium rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] disabled:hover:scale-100"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {route || isEditMode ? 'Update Route' : 'Create Route'}
                  </div>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RouteForm;